import React, { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import Tooltip from './Tooltip';

// Register the zoom plugin
Chart.register(zoomPlugin);

const ChartModal = ({ isOpen, onClose, chartConfig, title, initialDatasetVisibility = [], onVisibilityChange }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Create chart when modal opens
    useEffect(() => {
        if (!isOpen || !chartConfig) return;

        // Cleanup existing chart
        if (chartInstance.current) {
            chartInstance.current.destroy();
            chartInstance.current = null;
        }

        // Wait for modal to fully render
        const timeout = setTimeout(() => {
            if (chartRef.current && isOpen && !chartInstance.current) {
                try {
                    // Create full-featured chart for modal
                    const modalConfig = {
                        ...chartConfig,
                        options: {
                            ...chartConfig.options,
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                ...chartConfig.options.plugins,
                                title: {
                                    ...chartConfig.options.plugins?.title,
                                    display: true // Show title in modal
                                }
                            }
                        }
                    };
                    chartInstance.current = new Chart(chartRef.current, modalConfig);
                    
                    // Apply initial dataset visibility state if provided
                    if (initialDatasetVisibility.length > 0) {
                        initialDatasetVisibility.forEach((isVisible, index) => {
                            if (index < chartInstance.current.data.datasets.length) {
                                chartInstance.current.setDatasetVisibility(index, isVisible);
                            }
                        });
                        chartInstance.current.update();
                    }
                } catch (error) {
                    console.error('Error creating modal chart:', error);
                }
            }
        }, 200);

        return () => clearTimeout(timeout);
    }, [isOpen, chartConfig, initialDatasetVisibility]);

    // Cleanup chart when modal closes or unmounts
    useEffect(() => {
        if (!isOpen && chartInstance.current) {
            chartInstance.current.destroy();
            chartInstance.current = null;
        }
    }, [isOpen]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    // Enhanced reset function that resets both zoom and dataset visibility
    const handleEnhancedReset = () => {
        if (chartInstance.current) {
            // Reset zoom
            chartInstance.current.resetZoom();
            
            // Reset dataset visibility (show all sales reps)
            const resetVisibilityState = chartInstance.current.data.datasets.map(() => true);
            chartInstance.current.data.datasets.forEach((dataset, index) => {
                chartInstance.current.setDatasetVisibility(index, true);
            });
            
            // Update the chart to reflect changes
            chartInstance.current.update();
            
            // Notify compact chart of the visibility change
            if (onVisibilityChange) {
                onVisibilityChange(resetVisibilityState);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
                    <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                    <div className="flex items-center gap-3">
                        {/* Zoom Controls */}
                        <div className="flex gap-2">
                            <Tooltip text="Zoom in on chart data">
                                <button
                                    onClick={() => chartInstance.current?.zoom(1.2)}
                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                >
                                    Zoom In
                                </button>
                            </Tooltip>
                            <Tooltip text="Zoom out to see more data">
                                <button
                                    onClick={() => chartInstance.current?.zoom(0.8)}
                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                >
                                    Zoom Out
                                </button>
                            </Tooltip>
                            <Tooltip text="Reset zoom and show all sales reps">
                                <button
                                    onClick={handleEnhancedReset}
                                    className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                                >
                                    Reset
                                </button>
                            </Tooltip>
                        </div>
                        
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-2"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Chart Content */}
                <div className="flex-1 p-6 min-h-0">
                    <div style={{ height: '650px', maxHeight: '75vh' }}>
                        <canvas 
                            ref={chartRef} 
                            style={{ 
                                display: 'block',
                                width: '100%', 
                                height: '100%'
                            }}
                        ></canvas>
                    </div>
                    <div className="mt-4 text-sm text-gray-600 flex-shrink-0">
                        💡 <strong>Tip:</strong> Use mouse wheel to zoom, drag to pan, or use the zoom buttons above
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChartModal;